import React, { Component } from 'react'
import { Table, Button, TableColumnType, Input, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Checkbox from 'antd/lib/checkbox/Checkbox'

export interface ExtendedTableColumnType<T> extends TableColumnType<T> {
  searchable?: boolean
}

export type SortByOptions = {
  columnKey: string,
  direction: 'ascend' | 'descend'
}

export type TransferTableProps = {
  rowKey: string,
  columns: ExtendedTableColumnType<any>[],
  dataSource: any[],
  sortBy?: SortByOptions,
  pagination?: boolean,
  onChange: (selected: any[]) => void
  removeSelectAll?: boolean
}

export class TransferTable extends Component<TransferTableProps> {

  state = {
    selected: [] as any[]
  }

  private searchFiltered = new Set<any>()

  public clearSelection = () => {
    this.setState({
      selected: []
    })
  }

  private handleSelectAll = () => {
    const { rowKey, dataSource, onChange } = this.props
    let { selected } = this.state
    let keys = dataSource.map(ds => ds[rowKey])

    // If we are filtered, we should select then instead
    if (this.searchFiltered.size > 0) {
      keys = []
      this.searchFiltered.forEach(v => keys.push(v))
    }

    if (this.isSelectedAll) {
      selected = []
    } else {
      selected = keys
    }

    this.setState({
      selected
    }, () => {
      onChange(selected)
    })
  }

  private get isSelectedAll() {
    const { rowKey, dataSource } = this.props
    const { selected } = this.state
    let keys = dataSource.map(ds => ds[rowKey])

    // If we are filtered, we should select then instead
    if (this.searchFiltered.size > 0) {
      keys = []
      this.searchFiltered.forEach(v => keys.push(v))
    }

    if (keys.length === 0) return false
    return keys.filter(v => !selected.includes(v)).length === 0
  }

  private isSelected(record: any) {
    const { rowKey } = this.props
    const { selected } = this.state
    const value = record[rowKey]
    
    if (!value) return false

    return selected.includes(value)
  }

  private handleRowSelection = (record: any) => {
    const { rowKey, onChange } = this.props
    let { selected } = this.state
    const value = record[rowKey]
    
    if (value) {
      if (!this.isSelected(record)) {
        selected.push(value)
      } else {
        selected = selected.filter(v => v !== value)
      }

      this.setState({
        selected
      }, () => {
        onChange(selected)
      })
    }
  }

  private setupColumns = () => {
    const { columns } = this.props

    return columns.map(col => {
      if (col.searchable) {
        col.filterIcon = (filtered: any) => <SearchOutlined style={{ color: filtered ? '@primary-color' : undefined,fontSize: "18px" }} />
        col.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder='Search'
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button
                size="small"
                style={{ width: 90 }}
                onClick={() => {
                  this.searchFiltered.clear()
                  clearFilters()
                  confirm()
                }}
              >
                Reset
              </Button>
            </Space>
          </div>
        )

        // Frontend filter by search
        if (!col.onFilter) {
          col.onFilter = (value, record) => {
            const { rowKey } = this.props;
            const colKey = col.dataIndex?.toString();
        
            if (!colKey) return false;
        
            const keys = colKey.split(',');
            let recordValue;
        
            if (keys.length > 1) {
              recordValue = keys.reduce((acc, key) => acc && acc[key], record);
            } else {
              recordValue = record[colKey];
            }
        
            if (recordValue === undefined) return false;
        
            if (typeof value === 'string') {
              value = value.toLowerCase().trim();
            }
        
            const filtered = recordValue.toString().toLowerCase().includes(value);
            if (filtered) {
              this.searchFiltered.add(record[rowKey]);
            }
        
            return filtered;
          }
        }
      }

      return col
    })
  }

  private getColumns = () => {
    const { sortBy } = this.props
    const columns = this.setupColumns()

    if (sortBy) {
      const index = columns.findIndex(v => v.dataIndex === sortBy.columnKey)

      if (index >= 0) {
        columns[index] = {
          ...columns[index],
          defaultSortOrder: sortBy.direction
        }
      }
    }

    columns.unshift({
      title: () => {
        const { rowKey, dataSource } = this.props
        const { selected } = this.state
        const keys = dataSource.map(ds => ds[rowKey])

        const indeterminate = selected.length > 0 && selected.length < keys.length

        if(this.props.removeSelectAll){
          return <></>
        }

        return <Checkbox
          indeterminate={indeterminate}
          checked={this.isSelectedAll}
          onChange={this.handleSelectAll}
        />
      },
      dataIndex: 'selection',
      render: (text: any, record: any) => {

        return (
          <Checkbox
            checked={this.isSelected(record)}
            onChange={() => this.handleRowSelection(record)}
          />
        )
      }
    })

    return columns
  }

  render() {
    const { rowKey, dataSource, sortBy } = this.props
    
    if (sortBy) {
      dataSource.sort((a, b) => {
        const colA = a[sortBy.columnKey]
        const colB = b[sortBy.columnKey]

        if (sortBy.direction === 'ascend') {
          return colA - colB
        }

        return colB - colA
      })
    }
    console.log(this.getColumns())
    return (
      <Table
        rowKey={rowKey}
        columns={this.getColumns()}
        dataSource={dataSource}
        pagination={this.props.pagination as any}
        size="small"
      />
    )
  }
}